<template>
  <FormulateForm
    id="complaint"
    v-model="formData"
    method="post"
    @submit="submitHandler()"
  >
    <!-- Honeypots -->
    <FormulateInput
      name="bonusCode"
      type="text"
      :label="i18n?.form?.labels?.bonusCode || 'bonus code'"
      validation="nohtml"
      class="custom-form-element"
      aria-hidden="true"
    />
    <FormulateInput
      name="website"
      type="text"
      :label="i18n.form.labels.website"
      validation="nohtml"
      class="custom-form-element"
      aria-hidden="true"
    />

    <!-- Umbraco / infra -->
    <FormulateInput
      type="hidden"
      name="formToken"
      :value="formToken"
    />
    <FormulateInput
      type="hidden"
      name="__RequestVerificationToken"
    />
    <FormulateInput
      type="hidden"
      name="ufprt"
    />
    <FormulateInput
      type="hidden"
      name="label"
      :value="label"
    />
    <FormulateInput
      type="hidden"
      name="sublabel"
    />
    <FormulateInput
      type="hidden"
      name="locale"
      :value="locale"
    />
    <FormulateInput
      type="hidden"
      name="subject"
      :value="subjectValueForComplaints"
    />

    <!-- Person -->
    <FormulateInput
      :label="i18n.form.labels.name"
      name="name"
      validation="bail|required:trim|nohtml"
      :validation-messages="i18n.form.validations.name"
      error-behavior="blur"
    />

    <FormulateInput
      name="email"
      type="email"
      :label="i18n.form.labels.email"
      validation="bail|required:trim|email"
      :validation-messages="i18n.form.validations.email"
      error-behavior="blur"
    />

    <FormulateInput
      name="phonenumber"
      type="text"
      :label="i18n.form.labels.phonenumber"
      :validation="[formData.callbackCheckbox ? 'required' : null, 'nohtml']"
      :validation-messages="i18n.form.validations.phonenumber"
      error-behavior="blur"
    />

    <FormulateInput
      name="childname"
      :label="i18n.form.labels.childname"
      validation="nohtml"
      :validation-messages="i18n.form.validations.childname"
    />

    <!-- About (top-level) -->
    <FormulateInput
      v-show="!isAboutFieldPreselected"
      type="select"
      name="about"
      :options="aboutOptions"
      :label="i18n.form.labels.about"
      validation="required"
      :validation-messages="i18n.form.validations.about"
      error-behavior="blur"
      @input="onAboutChange"
    />

    <!-- Complaint subject (depends on about) -->
    <FormulateInput
      v-if="complaintSubjectOptions.length"
      :key="formData.about || 'no-about'"
      type="select"
      name="complaintSubject"
      :options="complaintSubjectOptions"
      :label="i18n.form.labels.complaintSubject"
      :validation="complaintSubjectOptions.length ? 'required' : ''"
      :validation-messages="i18n.form.validations.complaintSubject"
      error-behavior="blur"
    />

    <!-- Location details (match WizardAddChild.vue behavior) -->
    <fields-dropdown-cities
      :i18n="i18n"
      :label="label"
      field-name="city"
    />
    <fields-dropdown-city-locations
      v-show="formData.city"
      :i18n="i18n"
      :label="label"
      field-name="location"
      :is-required="true"
      :city="formData.city"
    />

    <!-- Remark -->
    <FormulateInput
      name="remark"
      type="textarea"
      :label="i18n.form.labels.remark"
      validation="bail|required:trim|nohtml"
      :validation-messages="i18n.form.validations.remark"
      error-behavior="blur"
    />

    <!-- Controls -->
    <fields-control-buttons
      :is-loading="isLoading"
      :i18n="i18n"
      @previous="previousHandler"
    />
  </FormulateForm>
</template>

<script>
import FieldsControlButtons from '@/components/forms/fields/FieldsControlButtons.vue';
import FieldsDropdownCities from '@/components/forms/fields/FieldsDropdownCities.vue';
import FieldsDropdownCityLocations from '@/components/forms/fields/FieldsDropdownCityLocations.vue';

import UmbracoFormMixin from '@/mixins/UmbracoForm';

export default {
  name: 'FormComplaint',
  components: {
    FieldsControlButtons,
    FieldsDropdownCities,
    FieldsDropdownCityLocations,
  },
  mixins: [UmbracoFormMixin],
  props: {
    uForm: {
      type: String,
      default: '',
    },
    label: {
      type: String,
      default: 'Partou',
    },
    i18n: {
      type: Object,
      default: null,
    },
    data: {
      type: Object,
      default: () => ({}),
    },
    locale: {
      type: String,
      default: null,
    },
    subjectValueForComplaints: {
      type: String,
      default: null,
    },
    about: {
      type: Array,
      default: () => [],
    },
    formToken: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      isLoading: false,
      formData: {
        callbackCheckbox: false,
        city: '',
        location: '',
        ...this.data,
      },
      isAboutFieldPreselected: false,
    };
  },
  computed: {
    aboutOptions() {
      if (Array.isArray(this.about) && this.about.length) {
        return this.about.map((o) => ({ value: o.value ?? '', label: o.label ?? '' }));
      }
      const opt = this.i18n?.form?.options?.about || {};
      if (Array.isArray(opt)) return opt;
      return Object.entries(opt).map(([value, label]) => ({ value, label }));
    },
    complaintSubjectOptions() {
      const selected = this.findAbout(this.formData.about);
      const subs = selected?.subCategories || [];
      return subs.map((s) => ({ value: s.value, label: s.label }));
    },
    callbackTimeOptions() {
      const options = this.i18n?.form?.options || {};
      const list = [
        options.callbackOption1,
        options.callbackOption2,
        options.callbackOption3,
        options.callbackOption4,
      ].filter(Boolean);
      return list.map((label, index) => ({ value: String(index + 1), label }));
    },
  },
  watch: {
    // Keep location consistent when city changes
    'formData.city'(val, oldVal) {
      if (val !== oldVal) this.formData.location = '';
    },
  },
  methods: {
    findAbout(value) {
      if (!Array.isArray(this.about)) return null;
      return this.about.find((o) => o.value === value) || null;
    },
    onAboutChange() {
      this.formData.complaintSubject = '';
    },
    onCallbackToggle(checked) {
      if (!checked) this.formData.callbackTime = '';
    },
    async submitHandler() {
      this.isLoading = true;
      this.$el.submit();
    },
    previousHandler() {
      window.history.go(-1);
    },
  },
};
</script>
